import { useState } from "react";
import {
  ArrowDown, BellRing, CalendarClock, CheckCircle2, ClipboardList, HandHeart, HardHat, HeartHandshake,
  MapPin, Megaphone, ShieldAlert, Users,
} from "lucide-react";
import { Hold, Tape, Topo } from "@/components/Brand";
import { DonationDialog } from "@/components/Dialogs";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const BASICS = [
  [HandHeart, "Gratis, donasi seikhlasnya", "Ikut manjat tidak dipungut biaya. Kalau mau berkontribusi, silakan lewat kotak donasi atau QRIS, besarnya terserah kamu."],
  [HardHat, "Alat sudah disiapkan", "Harness, sepatu panjat, dan tali tersedia. Donasi dari kalian dipakai untuk perawatan dan penggantian alat."],
  [Users, "Belayer dari tim kami", "Semua belayer malam ini adalah anggota tim iyalagi.Manjat."],
];

const QUEUE_STEPS = [
  "Pengambilan nomor antrean dibuka setelah briefing selesai. Masuk lewat iyalagimanjat.com dan setujui waiver.",
  "Check-in harus di lokasi. Saat HP meminta akses lokasi, tekan Izinkan. Kalau GPS bermasalah, minta keeper memindai QR kamu.",
  "Pilih jalur lalu ambil nomor. Satu profil hanya bisa punya satu nomor aktif, dan kuota nomor tiap sesi terbatas.",
  "Tidak perlu menatap layar terus. Kartu nomormu akan memberi tanda sendiri saat giliranmu mendekat. Aktifkan notifikasi supaya dapat pemberitahuan.",
];

const PREPARE = [
  "Segera menuju area pemasangan harness. Tim akan membantu memasang dan memeriksa harness-mu. Jangan lupa pakai sepatu panjat.",
  "Keluarkan isi kantong, ikat rambut yang panjang, dan copot aksesori yang berisiko tersangkut.",
  "Harness yang sudah terpasang jangan dilonggarkan atau dilepas sendiri sambil menunggu.",
  "Kalau sudah siap, tunggu di dekat dinding.",
];

const YOUR_TURN = [
  "Datangi dinding, sebutkan nama dan nomormu ke belayer, dan sampaikan kalau ini pertama kalinya kamu manjat.",
  "Sebelum kamu naik, tim akan memeriksa harness dan simpul talimu.",
  "Kalau saat dipanggil kamu tidak ada, keeper bisa melewati nomormu (status Dilewati). Segera lapor ke tim, atau ambil nomor baru.",
  "Pengin naik lagi? Setelah giliranmu selesai, ambil nomor baru dari halaman antrean yang sama.",
];

const LOWER = [
  "Teriakkan \u201cLower!\u201d dan tunggu hingga tali terasa kencang.",
  "Duduklah ke dalam harness dengan kaki lurus menempel di dinding.",
  "Melangkah mundur perlahan. Belayer yang akan menurunkanmu.",
  "Setelah menapak tanah, diam dulu sampai tim melepaskan simpul dari harness-mu. Jangan buka sendiri.",
];

const RULES = [
  "Hindari berdiri atau melintas di bawah orang yang sedang memanjat.",
  "Jangan menyentuh atau mengutak-atik tali dan perlengkapan yang sedang digunakan.",
  "Belum paham teknik belay? Jangan dicoba sendiri, minta tim mendampingi.",
  "Lokasi ini ruang publik, jadi jaga barang pribadimu masing-masing.",
];

function Numbered({ items, testid }) {
  return (
    <ol className="space-y-3" data-testid={testid}>
      {items.map((t, i) => (
        <li key={t} className="flex gap-3 text-[15px] leading-relaxed text-[#1A3323]/80">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#1A3323] font-mono text-xs font-extrabold text-[#FDFCF8]">{i + 1}</span>
          <span className="pt-0.5">{t}</span>
        </li>
      ))}
    </ol>
  );
}

function Bullets({ items, testid }) {
  return (
    <ul className="space-y-2.5" data-testid={testid}>
      {items.map((t) => (
        <li key={t} className="flex gap-2.5 text-[15px] leading-relaxed text-[#1A3323]/80">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#3D6F4B]" /> <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function TicketMock({ variant }) {
  const turn = variant === "turn";
  return (
    <div className={`grain relative overflow-hidden rounded-2xl bg-[#1A3323] p-4 text-[#FDFCF8] ${turn ? "ring-4 ring-[#B9D3A8]" : ""}`} aria-hidden="true">
      <Topo className="absolute -right-12 -top-8 h-32 w-48 text-white/10" />
      <div className="relative text-[10px] font-bold uppercase tracking-[0.22em] text-[#FDFCF8]/60">{turn ? "dipanggil" : "tiket kamu"}</div>
      <div className="relative font-mono text-3xl font-extrabold leading-tight">{turn ? "GILIRAN KAMU!" : "# A-012"}</div>
      {turn ? (
        <div className="relative mt-1 text-sm font-semibold text-[#FDFCF8]/80"># A-012 · Menuju Jalur A</div>
      ) : (
        <Tape className="relative mt-2 bg-[#B9D3A8]">Sebentar lagi naik, siap-siap!</Tape>
      )}
    </div>
  );
}

function StageCard({ variant, title, desc, items, testid }) {
  return (
    <div className="rounded-[22px] bg-white p-5 ring-1 ring-[#1A3323]/10" data-testid={testid}>
      <TicketMock variant={variant} />
      <h4 className="mt-4 text-lg font-extrabold text-[#1A3323]">{title}</h4>
      <p className="mt-1 text-sm text-[#1A3323]/65">{desc}</p>
      <div className="mt-4"><Bullets items={items} testid={`${testid}-list`} /></div>
    </div>
  );
}

const SECTIONS = [
  ["queue", ClipboardList, "Mengambil nomor antrean", "4 langkah", () => <Numbered items={QUEUE_STEPS} testid="briefing-queue-steps" />],
  ["status", BellRing, "Tanda di kartu antrean", "siap-siap & giliranmu", () => (
    <div className="grid gap-4 md:grid-cols-2">
      <StageCard variant="prepare" title="Siap-siap: sisa 5 orang lagi" desc="Kartu menampilkan tanda “Sebentar lagi naik” saat tinggal 5 orang atau kurang di depanmu." items={PREPARE} testid="briefing-stage-prepare" />
      <StageCard variant="turn" title="“Giliran kamu!”" desc="Kartu berubah jadi layar besar GILIRAN KAMU! saat keeper memanggil nomormu." items={YOUR_TURN} testid="briefing-stage-turn" />
    </div>
  )],
  ["lower", ArrowDown, "Cara turun yang benar", "4 langkah", () => <Numbered items={LOWER} testid="briefing-lower-steps" />],
  ["rules", ShieldAlert, "Aturan main di area panjat", "wajib dipatuhi", () => <Bullets items={RULES} testid="briefing-rules-list" />],
];

function BriefingAccordion() {
  return (
    <Accordion type="single" collapsible defaultValue="queue" className="space-y-3" data-testid="briefing-accordion">
      {SECTIONS.map(([id, Icon, title, meta, Body], i) => (
        <AccordionItem key={id} value={id} className="overflow-hidden rounded-[22px] border-0 bg-[#F7F4EA] ring-1 ring-[#1A3323]/10 transition-shadow data-[state=open]:shadow-md data-[state=open]:shadow-[#1A3323]/5" data-testid={`briefing-item-${id}`}>
          <AccordionTrigger className="px-5 py-4 hover:no-underline sm:px-6" data-testid={`briefing-trigger-${id}`}>
            <span className="flex items-center gap-4 text-left">
              <span className="font-mono text-sm font-extrabold text-[#3D6F4B]/70">0{i + 1}</span>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E3EDD9]"><Icon className="h-5 w-5 text-[#1A3323]" /></span>
              <span>
                <span className="block text-base font-extrabold text-[#1A3323] sm:text-lg">{title}</span>
                <span className="block text-xs font-medium text-muted-foreground">{meta}</span>
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-6 sm:px-6">
            <div className="border-t border-[#1A3323]/10 pt-5"><Body /></div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function BriefingIntro() {
  return (
    <div className="lg:sticky lg:top-8">
      <Tape>briefing pendatang baru</Tape>
      <h2 className="mt-4 text-4xl font-extrabold leading-tight text-[#1A3323] sm:text-5xl" data-testid="briefing-heading">Briefing Sebelum Manjat</h2>
      <p className="mt-4 text-lg font-bold text-[#1A3323]" data-testid="briefing-greeting">Halo, warga! Senang kamu ikut malam ini.</p>
      <p className="mt-2 text-base leading-relaxed text-[#1A3323]/70">
        Namanya iyalagi, karena habis turun dari dinding, jawabannya hampir selalu sama: &ldquo;Iya, lagi!&rdquo; Di sini pemula dan pemanjat berpengalaman berbagi dinding yang sama.
      </p>
      <div className="mt-5 space-y-2 text-sm font-semibold text-[#1A3323]" data-testid="briefing-schedule">
        <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#3D6F4B]" /> Setiap Senin, 18.30–22.00</div>
        <div className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#3D6F4B]" /> Plaza Festival Kuningan, depan Universitas Bakrie</div>
      </div>
    </div>
  );
}

function Basics({ onDonate }) {
  return (
    <div className="mt-10" data-testid="briefing-basics">
      <h3 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-[0.18em] text-[#3D6F4B]"><Megaphone className="h-4 w-4" /> Yang perlu kamu tahu dulu</h3>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {BASICS.map(([Icon, title, desc], i) => (
          <div key={title} className="rounded-[20px] bg-white p-5 ring-1 ring-[#1A3323]/10 transition-transform duration-300 hover:-translate-y-1" data-testid={`briefing-basic-${i + 1}`}>
            <Icon className="h-6 w-6 text-[#3D6F4B]" />
            <div className="mt-3 font-extrabold text-[#1A3323]">{title}</div>
            <p className="mt-1 text-sm leading-relaxed text-[#1A3323]/70">{desc}</p>
            {i === 0 && (
              <button onClick={onDonate} className="mt-3 inline-flex items-center gap-1.5 text-sm font-extrabold text-[#1A3323] underline decoration-[#B9D3A8] decoration-2 underline-offset-4 hover:decoration-[#3D6F4B]" data-testid="briefing-basic-donate-link">
                <HeartHandshake className="h-4 w-4" /> Lihat QRIS
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Closing({ onDonate }) {
  return (
    <div className="grain relative mt-10 overflow-hidden rounded-[28px] bg-[#1A3323] p-7 text-[#FDFCF8] sm:p-10" data-testid="briefing-closing">
      <Topo className="absolute -right-20 -top-16 h-72 w-[28rem] text-white/10" />
      <Hold className="absolute bottom-6 right-8 hidden h-10 w-12 opacity-70 sm:block" color="#B9D3A8" />
      <div className="relative max-w-xl">
        <h3 className="text-3xl font-extrabold sm:text-4xl">Selamat memanjat, warga!</h3>
        <p className="mt-3 text-base leading-relaxed text-[#FDFCF8]/75">
          Alat ini awet karena dirawat bersama, dan dirawat bersama karena kamu mau berbagi. Terima kasih, warga.
        </p>
        <Button onClick={onDonate} className="donate-glow mt-6 h-14 rounded-full bg-[#B9D3A8] px-7 text-base font-extrabold text-[#1A3323] hover:bg-[#cde2be]" data-testid="briefing-donate-button">
          <HeartHandshake className="heart-beat mr-2 h-5 w-5" /> Donasi lewat QRIS
        </Button>
      </div>
    </div>
  );
}

export function BriefingSection() {
  const [donate, setDonate] = useState(false);
  return (
    <section id="briefing" className="border-t border-[#1A3323]/10 bg-white py-16 sm:py-24" data-testid="briefing-section">
      <div className="mx-auto max-w-6xl px-6 lg:px-14">
        <div className="grid gap-10 lg:grid-cols-[.8fr_1.2fr]">
          <BriefingIntro />
          <BriefingAccordion />
        </div>
        <Basics onDonate={() => setDonate(true)} />
        <Closing onDonate={() => setDonate(true)} />
      </div>
      <DonationDialog open={donate} onOpenChange={setDonate} publicMode />
    </section>
  );
}
